// ============================================================================
// Entrada do Vite.
//
// Espelha a arvore de providers de src/pages/_app.tsx SEM alterar aquele
// arquivo, para o Next continuar funcionando em paralelo durante a migracao.
// Quando o Next for removido, _app.tsx e deletado e este passa a ser o unico
// ponto de entrada.
//
// Diferencas deliberadas em relacao ao _app.tsx:
//
// 1. SEM <PersistGate>. Descobrimos que ele era o motivo de o SSR do Next
//    devolver <div id="__next"> vazio: renderiza `loading={null}` no servidor e
//    nunca reidrata la. Como aqui nao existe SSR, ele so atrasaria o primeiro
//    render. O Redux tambem e vestigial no projeto (nenhum useSelector/
//    useDispatch em codigo de feature), e sai numa limpeza posterior.
//
// 2. SEM getInitialProps. Era o que desligava a Automatic Static Optimization
//    do Next inteiro. As settings sao lidas no cliente.
//
// 3. Router do react-router no lugar do roteamento por arquivos - ver
//    src/app-routes.tsx, que reproduz a convencao de pastas do Next.
// ============================================================================

// i18n
import './locales/i18n';

// highlight
import './utils/highlight';

// scroll bar
import 'simplebar';
import 'simplebar/dist/simplebar.css';
// map
import 'mapbox-gl/dist/mapbox-gl.css';
// editor - react-quill-new (o react-quill original usa findDOMNode, removido no React 19)
import 'react-quill-new/dist/quill.snow.css';
// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

// Fontes AUTO-HOSPEDADAS (nao pelo CDN do Google Fonts): se a maquina do cliente
// nao tiver saida livre, o CDN falha calado e a interface cai para system-ui.
//
// A MANROPE SAIU DAQUI (09/09/2026). Ela era baixada em toda abertura do sistema -
// 5 arquivos woff2, 72 KB no build - e NUNCA era desenhada: a pilha `sans` do
// tailwind.config comeca em Inter e nao menciona Manrope em lugar nenhum. Sobra de
// quando o projeto "declarava Manrope" sem nunca ter baixado um arquivo dela; ao
// hospedar as fontes de verdade, o import ficou e a fonte nao entrou na pilha.
// Conferido nos assets do build antes de remover.
//
// A IBM PLEX MONO SAIU (10/09/2026, a pedido de "tudo no padrao").
//
// Ela era a segunda familia da interface e aparecia em 24 lugares via
// `font-mono`: CPF, IMEI, serial, device id, nome de pacote. O usuario a
// descreveu como "fonte de maquina de escrever" - e e mesmo: a Plex Mono tem
// serifa nas hastes, entao no meio de um formulario em Inter ela nao le como
// "dado tecnico", le como fonte errada.
//
// O que ela entregava de util era o alinhamento de digito. Isso a Inter faz com
// `font-variant-numeric: tabular-nums`, que foi o que ficou no lugar - mesma
// largura por algarismo, uma familia so na tela.
//
// Sao 3 woff2 e ~45 KB que deixam de ser baixados em toda abertura.

// Tokens de design ANTES do global.css: o global.css tem o @tailwind base, e o
// Preflight do Tailwind precisa vir depois das declaracoes de :root para nao
// haver duvida de ordem em cascata.
/* A fonte vem ANTES dos tokens e do global: o @font-face precisa estar registrado
   quando as regras que pedem `font-family: Inter` forem aplicadas. */
import './theme/fontes.css';
import './theme/tokens.css';
import './theme/global.css';

import * as React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { Provider as ReduxProvider } from 'react-redux';
/* LocalizationProvider/AdapterDateFns SAIRAM com o MUI (19/08/2026).
   Eles serviam ao `RHFPicker`, o campo de data do template - usado apenas pelas
   quatro telas antigas, que foram removidas. Sem consumidor, o provider so
   arrastava @mui/x-date-pickers e date-fns para o bundle. */
import { Wrapper } from '@googlemaps/react-wrapper';
import cookie from 'cookie';

import { store } from './redux/store';
import { getSettings } from './utils/settings';
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
import { AuthProvider } from './contexts/JWTContext';
import { AppProvider } from './contexts/AppContext';
import { ChartStyle } from './components/chart';
import RtlLayout from './components/RtlLayout';
import ProgressBar from './components/ProgressBar';
import ThemeColorPresets from './components/ThemeColorPresets';
import NotistackProvider from './components/NotistackProvider';
import ThemeLocalization from './components/ThemeLocalization';
import MotionLazyContainer from './components/animate/MotionLazyContainer';

import AparenciaTokens from './theme/AparenciaTokens';
import AppRoutes from './app-routes';
import { AbasProvider } from './layouts/dashboard/abas/AbasContexto';

// Mesma chave usada no _app.tsx.
const GOOGLE_MAPS_API_KEY = 'AIzaSyDltCDiY7YrALNCV99zOs1XOGyVQ3GstII';

/* A ENGRENAGEM DE APARENCIA FOI REMOVIDA (10/09/2026, a pedido).

   Ela abria um painel com quatro ajustes, e tres deles nao eram decisao de
   ninguem: LARGURA passou a ser sempre total, COR e sempre a primeira (o
   azul-marinho do sistema) e LAYOUT nunca foi trocado. Manter um controle para
   cada um custava um botao flutuante permanente sobre o conteudo, um drawer, e
   a duvida de quem abria aquilo achando que precisava configurar algo antes de
   comecar.

   O unico ajuste que sobrou - claro/escuro - virou um botao na faixa superior
   (layouts/dashboard/header/BotaoTema.tsx).

   `utils/settings.js` passou a IGNORAR os cookies de cor e de largura: sem
   painel, quem tivesse escolhido roxo ou largura limitada algum dia ficaria
   preso naquilo para sempre, sem caminho de volta. */

function Raiz() {
  // No _app.tsx as settings vinham de getInitialProps (cookie lido no servidor).
  // Sem SSR, lemos no cliente. getSettings espera o cookie JA PARSEADO (objeto),
  // nao a string bruta - mesmo contrato usado em _app.tsx:191.
  const settings = React.useMemo(() => getSettings(cookie.parse(document.cookie || '')), []);

  return (
    // BrowserRouter precisa ser o provider MAIS EXTERNO.
    // Motivo: varios contexts (AuthProvider/JWTContext e afins) importam
    // "next/router", que o alias do Vite manda para o shim src/compat/next-router,
    // e ele usa useNavigate() do react-router. Com o Router aninhado no fundo da
    // arvore, esses providers rodavam fora do contexto e a aplicacao morria com
    // "useNavigate() may be used only in the context of a <Router> component" -
    // tela branca, sem render nenhum.
    // RecoilRoot removido em 13/08/2026: o unico consumidor de Recoil no projeto
    // era src/recoil/modal.ts, que importava tres modulos INEXISTENTES
    // (pages/components/AlertModal e afins) - codigo morto com import quebrado.
    // Com ele apagado, a pasta src/recoil ficou vazia e o provider nao envolvia
    // nada que usasse Recoil.
    <BrowserRouter>
      <AuthProvider>
        <ReduxProvider store={store}>
          <CollapseDrawerProvider>
            <SettingsProvider defaultSettings={settings}>
              {/* O ThemeProvider do MUI saiu daqui. Ele criava o tema do template
                  (palette, typography, shadows e 40 arquivos de `overrides`) e o
                  CssBaseline. Nada mais no projeto le esse tema: a aparencia vem
                  dos tokens CSS (theme/tokens.css) aplicados por classes do
                  Tailwind, e o reset e o preflight do proprio Tailwind.
                  `AparenciaTokens`, logo abaixo, continua sendo quem projeta a
                  escolha de tema e de cor do SettingsContext nos `data-*` do
                  <html> - e disso os tokens dependem. */}
              <NotistackProvider>
                    <AppProvider>
                      <MotionLazyContainer>
                        <ThemeColorPresets>
                          <ThemeLocalization>
                            <RtlLayout>
                              {/* Projeta o tema do SettingsContext nos data-* do
                                  <html> que os tokens de design leem. Fica dentro
                                  do SettingsProvider por depender dele, e nao
                                  renderiza nada. */}
                              <AparenciaTokens />
                              <ChartStyle />
                              {/* A ENGRENAGEM DE APARENCIA SO DENTRO DO DASHBOARD
                                  (10/09/2026, a pedido de tirar do login).

                                  Ela era global e flutuava tambem na tela de entrada,
                                  onde nao faz sentido: nao ha o que personalizar antes
                                  de entrar, e um botao que abre um painel de cores por
                                  cima do formulario de senha so tira o foco de quem
                                  esta tentando entrar.

                                  NAO foi removida do sistema: dentro do dashboard ela
                                  continua sendo o unico caminho para tema escuro, cor
                                  da paleta e largura. Tirar de la seria perder a
                                  funcao, nao limpar a tela. */}
                              <ProgressBar />
                              <Wrapper apiKey={GOOGLE_MAPS_API_KEY} libraries={['places', 'geometry']} language="pt-br">
                                {/* As abas guardam as telas abertas MONTADAS, entao o
                                    provider precisa ficar acima das rotas: dentro do
                                    layout, uma remontagem dele apagaria a lista e o
                                    cache. Precisa estar dentro do BrowserRouter,
                                    porque le a localizacao atual. */}
                                <AbasProvider>
                                  <AppRoutes />
                                </AbasProvider>
                              </Wrapper>
                            </RtlLayout>
                          </ThemeLocalization>
                        </ThemeColorPresets>
                      </MotionLazyContainer>
                    </AppProvider>
              </NotistackProvider>
            </SettingsProvider>
          </CollapseDrawerProvider>
        </ReduxProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<Raiz />);
